import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Alert, Box, Button, Checkbox, Chip, Paper, Stack, TextField, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
	DataGrid,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { downloadCofoFranchiseReportExcel, downloadCofoMonthLevelStoreSummaryExcel } from "../excelTemplateCOFOReport";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../EboPaymentWorking";

const filterControlSx = {
	width: "100%",
	"& .MuiInputBase-root": {
		height: 38,
		borderRadius: 1.5,
		backgroundColor: "#fff",
		fontSize: 13.5,
	},
	"& .MuiInputBase-input": {
		py: 0,
		fontSize: 13.5,
	},
	"& .MuiInputLabel-root": {
		fontSize: 13,
	},
};

const actionButtonSx = {
	height: 38,
	borderRadius: 1.5,
	px: 2,
	minWidth: 124,
	fontSize: 12.5,
	fontWeight: 600,
	boxShadow: "none",
	whiteSpace: "nowrap",
};

const ReportToolbar = React.memo(() => (
	<GridToolbarContainer>
		<GridToolbarColumnsButton />
		<GridToolbarFilterButton />
		<GridToolbarDensitySelector />
		<Box sx={{ flexGrow: 1 }} />
		<GridToolbarQuickFilter />
	</GridToolbarContainer>
));

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	if (data && typeof data === "object") {
		const firstArrayValue = Object.values(data).find(Array.isArray);
		if (firstArrayValue) return firstArrayValue;
	}
	return [];
};

const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const normalizeText = (value = "") => String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const getNormalizedKeys = (row = {}) =>
	Object.keys(row || {}).reduce((accumulator, key) => {
		accumulator[normalizeText(key)] = key;
		return accumulator;
	}, {});
const getMatchedFieldName = (rows, candidates = []) => {
	const normalizedCandidates = candidates.map(normalizeText);
	for (const row of rows) {
		const normalizedKeys = getNormalizedKeys(row);
		const matchedKey = normalizedCandidates.map((candidate) => normalizedKeys[candidate]).find(Boolean);
		if (matchedKey) return matchedKey;
	}
	return "";
};
const buildUniqueOptions = (rows, fieldName) => {
	if (!fieldName) return [];
	const optionMap = new Map();
	rows.forEach((row, index) => {
		const label = String(row?.[fieldName] ?? "").trim();
		if (!label) return;
		const key = normalizeText(label);
		if (!optionMap.has(key)) {
			optionMap.set(key, {
				id: `${fieldName}-${index + 1}`,
				label,
				value: label,
			});
		}
	});
	return Array.from(optionMap.values()).sort((first, second) => first.label.localeCompare(second.label));
};

const getFranchiseName = (row) => row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "";
const SELECT_ALL_FRANCHISE_OPTION = {
	id: "__select_all__",
	label: "Select All",
	value: "__select_all__",
	selectAll: true,
};
const REPORT_FRANCHISE_CANDIDATES = ["franchise_name", "franchise", "name", "franchiseName"];
const REPORT_COMPONENT_CANDIDATES = ["component", "component_name", "component_type", "componentType", "report_component"];
const waitForNextPaint = () =>
	new Promise((resolve) => {
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(resolve);
		});
	});

function COFOReport() {
	const [filters, setFilters] = useState({
		from_month_year: null,
		to_month_year: null,
		franchises: [],
	});
	const [franchiseOptions, setFranchiseOptions] = useState([]);
	const [reportData, setReportData] = useState({
		report: [],
		salary: [],
		petty_cash: [],
	});
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [downloadingMonthSummary, setDownloadingMonthSummary] = useState(false);
	const [error, setError] = useState("");
	const [summaryFilters, setSummaryFilters] = useState({
		franchises: [],
		components: [],
	});

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const roleAccessData = roleAccess(user);

	useEffect(() => {
		const fetchFranchiseOptions = async () => {
			try {
				setLoadingOpen(true);
				const res = await pythonVmInstance({
					url: "/ebo-payment-working/cofo/mapped-stores",
					headers: { "Content-Type": "application/json" },
					method: "GET",
				});
				const uniqueFranchises = new Map();
				getResponseRows(res?.data).forEach((row, index) => {
					const franchiseName = getFranchiseName(row);
					if (!franchiseName) return;
					const key = normalizeText(franchiseName);
					if (!uniqueFranchises.has(key)) {
						uniqueFranchises.set(key, {
							id: row?.id ?? row?._id ?? `franchise-${index + 1}`,
							label: franchiseName,
							value: franchiseName,
						});
					}
				});
				setFranchiseOptions(Array.from(uniqueFranchises.values()).sort((first, second) => first.label.localeCompare(second.label)));
			} catch (fetchError) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: fetchError?.response?.data?.message || "Failed to fetch COFO franchises",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			} finally {
				setLoadingOpen(false);
			}
		};

		fetchFranchiseOptions();
	}, [setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]);

	const tableRows = useMemo(
		() =>
			getResponseRows(reportData?.report).map((row, index) => ({
				...(row && typeof row === "object" ? row : { value: row }),
				id: row?.id ?? row?._id ?? `${row?.franchise_name || row?.franchise || "cofo"}-${index + 1}`,
			})),
		[reportData]
	);

	const summaryFranchiseField = useMemo(
		() => getMatchedFieldName(tableRows, REPORT_FRANCHISE_CANDIDATES),
		[tableRows]
	);
	const summaryComponentField = useMemo(
		() => getMatchedFieldName(tableRows, REPORT_COMPONENT_CANDIDATES),
		[tableRows]
	);

	const summaryFranchiseOptions = useMemo(
		() => buildUniqueOptions(tableRows, summaryFranchiseField),
		[tableRows, summaryFranchiseField]
	);

	const selectedSummaryFranchiseSet = useMemo(
		() => new Set(summaryFilters.franchises.map((selected) => normalizeText(selected.value))),
		[summaryFilters.franchises]
	);

	const selectedSummaryComponentSet = useMemo(
		() => new Set(summaryFilters.components.map((selected) => normalizeText(selected.value))),
		[summaryFilters.components]
	);

	const summaryComponentOptions = useMemo(() => {
		const sourceRows =
			selectedSummaryFranchiseSet.size && summaryFranchiseField
				? tableRows.filter((row) => selectedSummaryFranchiseSet.has(normalizeText(row?.[summaryFranchiseField] ?? "")))
				: tableRows;
		return buildUniqueOptions(sourceRows, summaryComponentField);
	}, [tableRows, selectedSummaryFranchiseSet, summaryComponentField, summaryFranchiseField]);

	useEffect(() => {
		setSummaryFilters((current) => ({
			franchises: current.franchises.filter((selected) =>
				summaryFranchiseOptions.some((option) => normalizeText(option.value) === normalizeText(selected.value))
			),
			components: current.components.filter((selected) =>
				summaryComponentOptions.some((option) => normalizeText(option.value) === normalizeText(selected.value))
			),
		}));
	}, [summaryComponentOptions, summaryFranchiseOptions]);

	const filteredSummaryRows = useMemo(
		() =>
			tableRows.filter((row) => {
				const franchiseMatch =
					!selectedSummaryFranchiseSet.size ||
					!summaryFranchiseField ||
					selectedSummaryFranchiseSet.has(normalizeText(row?.[summaryFranchiseField] ?? ""));
				const componentMatch =
					!selectedSummaryComponentSet.size ||
					!summaryComponentField ||
					selectedSummaryComponentSet.has(normalizeText(row?.[summaryComponentField] ?? ""));
				return franchiseMatch && componentMatch;
			}),
		[selectedSummaryComponentSet, selectedSummaryFranchiseSet, tableRows, summaryComponentField, summaryFranchiseField]
	);

	const columns = useMemo(() => {
		const fields = Array.from(new Set(tableRows.flatMap((row) => Object.keys(row).filter((field) => field !== "id"))));
		if (!fields.length) return [];

		return fields
			.map((field) => ({
				field,
				headerName: formatHeaderName(field),
				flex: 1,
				minWidth: 150,
				valueFormatter: (value) => formatCellValue(value?.value ?? value),
			}));
	}, [tableRows]);

	const allFranchisesSelected =
		franchiseOptions.length > 0 &&
		filters.franchises.length === franchiseOptions.length &&
		franchiseOptions.every((option) => filters.franchises.some((selected) => selected.value === option.value));

	const handleGetReport = async () => {
		if (!filters.from_month_year || !filters.to_month_year) {
			setError("Please select both From and To month-year.");
			return;
		}
		if (!filters.franchises.length) {
			setError("Please select at least one franchise.");
			return;
		}
		if (dayjs(filters.from_month_year).isAfter(dayjs(filters.to_month_year))) {
			setError("From Month-Year must be earlier than or equal to To Month-Year.");
			return;
		}

		try {
			setLoading(true);
			setLoadingOpen(true);
			setError("");
			const payload = {
				store_type: "cofo",
				start_date: dayjs(filters.from_month_year).startOf("month").format("YYYY-MM-DD"),
				end_date: dayjs(filters.to_month_year).endOf("month").format("YYYY-MM-DD"),
				franchise: filters.franchises.map((item) => item.value),
			};
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/cofo/franchise-report",
				headers: { "Content-Type": "application/json" },
				data: payload,
				method: "POST",
			});
			setReportData({
				report: getResponseRows(res?.data?.report),
				salary: getResponseRows(res?.data?.salary),
				petty_cash: getResponseRows(res?.data?.petty_cash),
			});
			setSummaryFilters({ franchises: [], components: [] });
		} catch (fetchError) {
			setReportData({
				report: [],
				salary: [],
				petty_cash: [],
			});
			setError(fetchError?.response?.data?.message || "Failed to fetch COFO report.");
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: fetchError?.response?.data?.message || "Failed to fetch COFO report",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
			setLoadingOpen(false);
		}
	};

	const handleDownload = async () => {
		if (!tableRows.length) return;
		try {
			setDownloading(true);
			setLoadingOpen(true);
			await waitForNextPaint();
			await downloadCofoFranchiseReportExcel({
				reportRows: getResponseRows(reportData?.report),
				salaryRows: getResponseRows(reportData?.salary),
				pettyCashRows: getResponseRows(reportData?.petty_cash),
				fromDate: filters.from_month_year,
				toDate: filters.to_month_year,
				selectedFranchises: filters.franchises.map((item) => item.value),
			});
			setLoadingOpen(false);
		} catch (downloadError) {
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: downloadError?.message || "Failed to download COFO report",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setDownloading(false);
			// setLoadingOpen(false);
		}
	};

	const handleMonthLevelStoreSummaryDownload = async () => {
		if (!tableRows.length) return;
		try {
			setDownloadingMonthSummary(true);
			setLoadingOpen(true);
			await waitForNextPaint();
			await downloadCofoMonthLevelStoreSummaryExcel({
				reportRows: getResponseRows(reportData?.report),
				salaryRows: getResponseRows(reportData?.salary),
				pettyCashRows: getResponseRows(reportData?.petty_cash),
				fromDate: filters.from_month_year,
				toDate: filters.to_month_year,
			});
		} catch (downloadError) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: downloadError?.message || "Failed to download month level store summary",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setDownloadingMonthSummary(false);
			setLoadingOpen(false);
		}
	};

	return (
		<Box
			width="100%"
			p={1.5}
			sx={{
				background:
					"radial-gradient(circle at top left, rgba(148,163,184,0.10) 0%, rgba(148,163,184,0.03) 24%, rgba(255,255,255,1) 58%), linear-gradient(180deg, #fafafa 0%, #ffffff 100%)",
			}}>
			<Stack spacing={1.5}>
				<Paper
					variant="outlined"
					sx={{
						p: 1.5,
						borderRadius: 2.5,
						borderColor: "rgba(148,163,184,0.25)",
						boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
						background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.94) 100%)",
					}}>
					<Stack spacing={1.5}>
						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", md: "row" },
								alignItems: { xs: "flex-start", md: "center" },
								justifyContent: "space-between",
								gap: 1,
							}}>
							<Box>
								<Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: 0.2 }}>
									COFO Report Summary
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: 720 }}>
								Choose a month range and one or more franchises to get the report summary and download it.
								</Typography>
							</Box>
							<Chip
								label={`${filters.franchises.length || 0} Franchise${filters.franchises.length === 1 ? "" : "s"}`}
								size="small"
								variant="outlined"
								sx={{
									height: 28,
									borderRadius: 999,
									fontSize: 11.5,
									bgcolor: "rgba(255,255,255,0.75)",
									borderColor: "rgba(148,163,184,0.35)",
								}}
							/>
						</Box>

						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr auto auto auto" },
									gap: 1.25,
									alignItems: "stretch",
								}}>
								<DatePicker
									views={["year", "month"]}
									openTo="month"
									label="From Month-Year"
									value={filters.from_month_year}
									onChange={(newValue) => {
										setFilters((prev) => ({
											...prev,
											from_month_year: newValue,
										}));
										setError("");
									}}
									slotProps={{
										textField: {
											size: "small",
											sx: filterControlSx,
										},
									}}
									format="MMM YYYY"
								/>

								<DatePicker
									views={["year", "month"]}
									openTo="month"
									label="To Month-Year"
									value={filters.to_month_year}
									onChange={(newValue) => {
										setFilters((prev) => ({
											...prev,
											to_month_year: newValue,
										}));
										setError("");
									}}
									minDate={filters.from_month_year ? dayjs(filters.from_month_year).startOf("month") : undefined}
									maxDate={dayjs().endOf("month")}
									slotProps={{
										textField: {
											size: "small",
											sx: filterControlSx,
										},
									}}
									format="MMM YYYY"
								/>

								<Autocomplete
									multiple
									disableCloseOnSelect
									options={[SELECT_ALL_FRANCHISE_OPTION, ...franchiseOptions]}
									value={filters.franchises}
									onChange={(_, newValue) => {
										const hasSelectAll = newValue.some((item) => item.selectAll);
										if (hasSelectAll) {
											setFilters((prev) => ({
												...prev,
												franchises: allFranchisesSelected ? [] : franchiseOptions,
											}));
											setError("");
											return;
										}

										setFilters((prev) => ({
											...prev,
											franchises: newValue.filter((item) => !item.selectAll),
										}));
										setError("");
									}}
									getOptionLabel={(option) => option?.label || ""}
									isOptionEqualToValue={(option, value) => option?.value === value?.value}
									renderOption={(props, option, { selected }) => (
										<li {...props} key={option.value}>
											<Checkbox sx={{ mr: 0.75, p: 0.4 }} checked={option.selectAll ? allFranchisesSelected : selected} />
											{option.label}
										</li>
									)}
									renderTags={(value) =>
										value.length ? (
											<Typography variant="body2" sx={{ px: 0.25, fontSize: 13, color: "text.primary" }}>
												{value.length} franchise{value.length === 1 ? "" : "s"} selected
											</Typography>
										) : null
									}
									renderInput={(params) => <TextField {...params} label="Select Franchise" size="small" placeholder="Choose one or more franchises" />}
									sx={{
										...filterControlSx,
										minWidth: 280,
										"& .MuiInputBase-root": {
											...filterControlSx["& .MuiInputBase-root"],
											minHeight: 38,
											alignItems: "center",
											py: 0.2,
											pr: 4.5,
											overflow: "hidden",
										},
										"& .MuiAutocomplete-input": {
											fontSize: 13.5,
											minWidth: 30,
										},
										"& .MuiAutocomplete-inputRoot": {
											flexWrap: "nowrap",
											overflow: "hidden",
										},
										"& .MuiAutocomplete-tag": {
											display: "none",
										},
										"& .MuiAutocomplete-endAdornment": {
											right: 8,
										},
									}}
								/>

								<Button
									variant="contained"
									onClick={handleGetReport}
									disabled={loading || downloading || !filters.from_month_year || !filters.to_month_year || filters.franchises.length === 0}
									size="small"
									sx={{
										...actionButtonSx,
										background: "linear-gradient(180deg, #191b1d 0%, #4e5053 100%)",
										color: "#f0f0f0",
										border: "1px solid rgba(203,213,225,0.9)",
										"&:hover": { background: "linear-gradient(180deg, #191b1d 0%, #4e5053 100%)", boxShadow: "none" },
										"&.Mui-disabled": {
											background: "linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)",
											color: "#ababab",
										},
									}}>
									{loading ? "Fetching..." : "Get Report"}
								</Button>

								<Button
									variant="outlined"
									startIcon={<FileDownloadIcon />}
									onClick={handleDownload}
									disabled={loading || downloading || downloadingMonthSummary || tableRows.length === 0 || roleAccessData.roles === "VIEWER"}
									size="small"
									sx={{
										...actionButtonSx,
										borderColor: "#0f766e",
										color: "#ffffff",
										backgroundColor: "#0f766e",
										"& .MuiButton-startIcon": { mr: 0.75 },
										"&:hover": {
											borderColor: "#115e59",
											backgroundColor: "#115e59",
										},
										"&.Mui-disabled": {
											borderColor: "#e7e7e7",
											color: "#c3c3c3",
											backgroundColor: "#f3f4f6",
										},
									}}>
									{downloading ? "Downloading..." : "Download"}
								</Button>

								<Button
									variant="outlined"
									startIcon={<FileDownloadIcon />}
									onClick={handleMonthLevelStoreSummaryDownload}
									disabled={loading || downloading || downloadingMonthSummary || tableRows.length === 0 || roleAccessData.roles === "VIEWER"}
									size="small"
									sx={{
										...actionButtonSx,
										borderColor: "#c2cce6",
										color: "#ffffff",
										backgroundColor: "#728acacb",
										"& .MuiButton-startIcon": { mr: 0.75 },
										"&:hover": {
											borderColor: "#c2cce6",
											backgroundColor: "#8292c4",
										},
										"&.Mui-disabled": {
											borderColor: "#e7e7e7",
											color: "#c3c3c3",
											backgroundColor: "#f3f4f6",
										},
									}}>
									{downloadingMonthSummary ? "Downloading..." : "Store Summary"}
								</Button>
							</Box>
						</LocalizationProvider>

						{error ? <Alert severity="error">{error}</Alert> : null}
					</Stack>
				</Paper>

				<Paper
					variant="outlined"
					sx={{
						p: 1.5,
						borderRadius: 2.5,
						borderColor: "rgba(148,163,184,0.2)",
						boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
						background: "rgba(255,255,255,0.96)",
					}}>
					<Stack spacing={1.5}>
						<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
							<Typography variant="subtitle1" fontWeight={700}>
								Report Summary
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Rows: {filteredSummaryRows.length.toLocaleString("en-IN")}
							</Typography>
						</Stack>

						{tableRows.length ? (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", md: summaryComponentField ? "1fr 1fr" : "1fr" },
									gap: 1,
								}}>
								{summaryFranchiseField ? (
									<Autocomplete
										multiple
										disableCloseOnSelect
										options={summaryFranchiseOptions}
										value={summaryFilters.franchises}
										onChange={(_, newValue) => {
											setSummaryFilters((prev) => ({
												...prev,
												franchises: newValue,
											}));
										}}
										getOptionLabel={(option) => option?.label || ""}
										isOptionEqualToValue={(option, value) => option?.value === value?.value}
										renderOption={(props, option, { selected }) => (
											<li {...props} key={option.value}>
												<Checkbox sx={{ mr: 0.75, p: 0.4 }} checked={selected} />
												{option.label}
											</li>
										)}
										renderTags={(value) =>
											value.length ? (
												<Typography variant="body2" sx={{ px: 0.25, fontSize: 13 }}>
													{value.length} franchise{value.length === 1 ? "" : "s"} selected
												</Typography>
											) : null
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Summary Franchise"
												size="small"
												placeholder="Filter rendered franchises"
											/>
										)}
										sx={filterControlSx}
									/>
								) : null}

								{summaryComponentField ? (
									<Autocomplete
										multiple
										disableCloseOnSelect
										options={summaryComponentOptions}
										value={summaryFilters.components}
										onChange={(_, newValue) => {
											setSummaryFilters((prev) => ({
												...prev,
												components: newValue,
											}));
										}}
										getOptionLabel={(option) => option?.label || ""}
										isOptionEqualToValue={(option, value) => option?.value === value?.value}
										renderOption={(props, option, { selected }) => (
											<li {...props} key={option.value}>
												<Checkbox sx={{ mr: 0.75, p: 0.4 }} checked={selected} />
												{option.label}
											</li>
										)}
										renderTags={(value) =>
											value.length ? (
												<Typography variant="body2" sx={{ px: 0.25, fontSize: 13 }}>
													{value.length} component{value.length === 1 ? "" : "s"} selected
												</Typography>
											) : null
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Summary Component"
												size="small"
												placeholder="Filter franchise components"
											/>
										)}
										sx={filterControlSx}
									/>
								) : null}
							</Box>
						) : null}

						<Box sx={{ height: { xs: "calc(100vh - 360px)", md: "calc(100vh - 300px)" }, minHeight: 360, width: "100%" }}>
							<DataGrid
								rows={filteredSummaryRows}
								columns={columns}
								loading={loading}
								slots={{ toolbar: ReportToolbar }}
								density="compact"
								disableRowSelectionOnClick
								pageSizeOptions={[25, 50, 100]}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 25, page: 0 },
									},
								}}
								sx={{
									border: "none",
									fontSize: 13,
									"& .MuiDataGrid-row": { minHeight: 36, maxHeight: 36 },
									"& .MuiDataGrid-cell": { py: 0.5 },
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f5f5f5",
										minHeight: 40,
										maxHeight: 40,
									},
								}}
							/>
						</Box>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default COFOReport;
